import { useEffect, useMemo, useState } from 'react';
import { publicApi } from '../api';
import { defaultHomeContent, localized, localizedField, mergeHomeContent } from '../lib/homeContent';
import { getCachedHomeContentSync, hasInitialHomeContent, loadHomeContent } from '../lib/homeContentLoader';
import { hasHomeSection } from '../lib/productHomeConfig';
import Header from '../components/Header';
import Footer from '../components/Footer';
import HomePageDeferredBoundary from './HomePageDeferredBoundary';
import HomeProductRowsBoundary from './HomeProductRowsBoundary';
import Seo from '../components/Seo';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../context/ToastContext';
import { SITE_URL } from '../api';
import { localizedPath } from '../lib/localeRoutes';
import { resolvePublicAssetUrl } from '../lib/assetUrl';

const fallbackMarquee = ['OMENASO', 'CELYNNEI', 'ROXICAKE'];
function getOptionalHref(raw, language = 'es') {
  const href = localized(raw, language, typeof raw === 'string' ? raw : '');
  if (typeof href !== 'string') return '';
  const trimmed = href.trim();
  return !trimmed || trimmed === '#' ? '' : localizedPath(trimmed, language);
}

function CreatorCard({ creator }) {
  const { language } = useLanguage();
  const creatorName = localized(creator.name, language, '');
  const creatorHref = getOptionalHref(creator.href, language);
  return (
    <a href={creatorHref || undefined} onClick={creatorHref ? undefined : (e) => e.preventDefault()} className="flex w-[130px] md:w-[188px] shrink-0 flex-col items-center justify-center gap-[10px] transition-transform hover:-translate-y-0.5">
      <img src={resolvePublicAssetUrl(creator.imageUrl)} alt={creatorName} width={169} height={169} loading="lazy" decoding="async" className="h-[120px] w-[120px] md:h-[169px] md:w-[169px] rounded-[15px] object-cover" />
      <h3 className="text-[14px] md:text-[18px] font-semibold text-black text-center leading-tight">{creatorName}</h3>
    </a>
  );
}

function CategoryCard({ item }) {
  const { language, t } = useLanguage();
  const name = localized(item.name, language, item.name || '');
  const description = localized(item.description, language, item.description || '');
  const itemHref = getOptionalHref(item.href, language);
  return (
    <div className="relative w-full overflow-hidden rounded-[25px] border border-[#E3E3E3] bg-white" style={{ aspectRatio: '653 / 276' }}>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="absolute -right-12 -top-12 h-[311px] w-[375px] rounded-full opacity-60 blur-[75px]" style={{ backgroundColor: item.bgColor || '#FFD9EB' }} />
      </div>

      <div className="relative z-10 flex h-full items-center justify-between px-5 md:px-8">
        <div className="flex w-[180px] md:w-[231px] shrink-0 flex-col justify-center gap-1 md:gap-[5px]">
          <h3 className="text-[18px] md:text-[22px] font-bold text-black leading-tight">{name}</h3>
          <p className="w-full text-[14px] md:text-[18px] font-normal leading-snug text-black">{description}</p>
          <a href={itemHref || undefined} onClick={itemHref ? undefined : (e) => e.preventDefault()} className="mt-1 md:mt-2 inline-flex h-[38px] md:h-[45px] w-full md:w-[213px] items-center justify-center gap-1.5 md:gap-2.5 rounded-[12px] md:rounded-[15px] bg-black text-[13px] md:text-[16px] font-bold text-white transition-colors hover:bg-[#FF50A3]">
            {t('home.viewCatalog')}
            <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="-rotate-45 w-[15px] h-[15px] md:w-[19px] md:h-[19px]">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
        </div>

        <div className="flex h-full flex-1 items-center justify-center overflow-hidden">
          <img src={resolvePublicAssetUrl(item.imageUrl)} alt={name} width={240} height={240} loading="lazy" decoding="async" className="max-h-[160px] md:max-h-[240px] w-auto object-contain" style={{ transform: 'rotate(6deg)' }} />
        </div>
      </div>
    </div>
  );
}

function HomePage() {
  const [content, setContent] = useState(() => getCachedHomeContentSync());
  const [homeProducts, setHomeProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(() => !hasInitialHomeContent());
  const [productsLoading, setProductsLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [requestedSlide, setRequestedSlide] = useState(0);
  const [hasCarouselStarted, setHasCarouselStarted] = useState(false);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const { language, t } = useLanguage();
  const { addToast } = useToast();

  useEffect(() => {
    let alive = true;

    const loadContent = async () => {
      try {
        const nextContent = await loadHomeContent();
        if (!alive) return;
        setContent(mergeHomeContent(nextContent));
      } catch {
        if (alive) setContent(defaultHomeContent);
      } finally {
        if (alive) setLoading(false);
      }
    };

    loadContent();

    const handleHomeContentUpdated = () => loadContent();
    window.addEventListener('home_content:updated', handleHomeContentUpdated);

    return () => {
      alive = false;
      window.removeEventListener('home_content:updated', handleHomeContentUpdated);
    };
  }, []);

  useEffect(() => {
    const key = 'newsletter:cta-toast';
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return;
      sessionStorage.removeItem(key);
      const parsed = JSON.parse(raw);
      if (parsed?.type === 'already') {
        addToast(language === 'en' ? 'You are already subscribed' : 'Ya estabas suscrito', 'success');
      } else if (parsed?.type === 'success') {
        addToast(language === 'en' ? 'Subscription completed' : 'Suscripción completada', 'success');
      } else if (parsed?.type === 'error') {
        addToast(language === 'en' ? 'Could not subscribe automatically' : 'No se pudo suscribir automáticamente', 'error');
      }
    } catch {
      // ignore malformed persisted toast
    }
  }, [addToast, language]);

  useEffect(() => {
    let alive = true;

    const loadCategories = async () => {
      try {
        const res = await publicApi.categories();
        if (!alive) return;
        const items = res.items || res.data || res || [];
        setCategories(Array.isArray(items) ? items : []);
      } catch {
        if (alive) setCategories([]);
      }
    };

    loadCategories();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    let alive = true;

    const loadProducts = async () => {
      setProductsLoading(true);
      try {
        const res = await publicApi.products({ limit: 100 });
        if (!alive) return;
        const items = res.items || res.data || res || [];
        const arr = Array.isArray(items) ? items : [];

        if (arr.length) {
          setHomeProducts(arr);
        } else if (content.merch?.items?.length) {
          setHomeProducts(content.merch.items.slice(0, content.merch?.featuredLimit || 3));
        } else {
          setHomeProducts([]);
        }
      } catch {
        if (alive) {
          if (content.merch?.items?.length) {
            setHomeProducts(content.merch.items.slice(0, content.merch?.featuredLimit || 3));
          } else {
            setHomeProducts([]);
          }
        }
      } finally {
        if (alive) setProductsLoading(false);
      }
    };

    loadProducts();

    return () => {
      alive = false;
    };
  }, [content.merch?.featuredLimit, content.merch?.items]);

  const categoryItems = useMemo(() => {
    if (content.categories?.items?.length) {
      return content.categories.items;
    }

    if (categories.length > 0) {
      return categories.slice(0, 4).map((category) => {
        const categoryName = localizedField(category, 'name', language, category.name || '');
        return {
          name: categoryName,
          description: localizedField(category, 'description', language, category.description || t('home.categoryDescription')),
          imageUrl: category.image || `https://placehold.co/640x420/FFD9EB/FFFFFF?text=${encodeURIComponent(categoryName)}`,
          bgColor: category.bgColor || '#FFB4D8',
          href: `/category/${category.slug}/`,
        };
      });
    }

    return [];
  }, [categories, content.categories?.items, language, t]);

  const marqueeItems = useMemo(() => {
    return fallbackMarquee;
  }, []);

  const marqueeLoopItems = useMemo(() => {
    const source = marqueeItems.length ? marqueeItems : fallbackMarquee;
    return Array.from({ length: Math.max(source.length * 3, 12) }, (_, index) => source[index % source.length]);
  }, [marqueeItems]);

  useEffect(() => {
    const slides = content.hero?.slides || [];
    if (slides.length <= 1) return;

    const interval = setInterval(() => {
      setHasCarouselStarted(true);
      setRequestedSlide((prev) => (prev + 1) % slides.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [content.hero?.slides]);

  const heroSlides = content.hero?.slides || [];
  const heroVariants = (() => {
    try {
      return JSON.parse(document.getElementById('home-hero-variants')?.textContent || 'null');
    } catch {
      return null;
    }
  })();
  const merchProducts = useMemo(() => {
    const items = homeProducts.filter((product) => hasHomeSection(product, 'MERCH'));
    return [...items].sort((a, b) => {
      const aOrder = a.merchOrder ?? Number.MAX_SAFE_INTEGER;
      const bOrder = b.merchOrder ?? Number.MAX_SAFE_INTEGER;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return String(b.createdAt || '').localeCompare(String(a.createdAt || ''));
    }).slice(0, content.merch?.featuredLimit || 10);
  }, [content.merch?.featuredLimit, homeProducts]);

  const latestProducts = useMemo(() => homeProducts.filter((product) => hasHomeSection(product, 'LAST_CHANCE')).slice(0, 4), [homeProducts]);
  const upcomingItems = useMemo(() => homeProducts.filter((product) => hasHomeSection(product, 'UPCOMING')).slice(0, 5), [homeProducts]);
  const heroImage = heroSlides[0]?.imageUrl || '/images/home/home_1x.webp';
  if (loading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-5 bg-[#FF50A3]">
        <img src={resolvePublicAssetUrl(content.header?.logoUrl || '/images/logo.png')} alt="MiskiPOP" width={211} height={62} decoding="async" fetchPriority="high" className="h-auto w-56 object-contain" />
        <div className="miski-loader-spinner" aria-hidden="true" />
        <p className="text-sm font-black tracking-wide text-white">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] font-['Poppins',sans-serif] text-slate-950">
      <Seo
        title={language === 'en' ? 'Official creator merch' : 'Merch oficial de creadores'}
        description={language === 'en' ? 'Discover official collections made with your favorite creators at MiskiPOP.' : 'Descubre colecciones oficiales creadas con tus creadores favoritos en MiskiPOP.'}
        canonical={`${SITE_URL}/`}
        image={heroImage}
        structuredDataId="home-seo"
        structuredData={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'Organization',
              name: 'MiskiPOP',
              url: SITE_URL,
              logo: `${SITE_URL}${content.header?.logoUrl || '/images/logo.png'}`
            },
            {
              '@type': 'WebSite',
              name: 'MiskiPOP',
              url: SITE_URL,
              potentialAction: {
                '@type': 'SearchAction',
                target: `${SITE_URL}/merch?search={search_term_string}`,
                'query-input': 'required name=search_term_string'
              }
            }
          ]
        }}
      />
      <Header />

      <main>
        {content.hero?.isActive !== false && (
          <section className="w-full pt-0 pb-6 md:pt-8 md:pb-10 px-2 sm:px-4 md:px-[45px]">
            <div className="relative w-full rounded-[25px] overflow-hidden" style={{ aspectRatio: isMobile ? '9 / 16' : '1345 / 503' }}>
              {heroSlides.length > 0 ? (
                <>
                  {heroSlides.map((slide, index) => {
                    const originalImage = isMobile ? (slide.mobileImageUrl || slide.imageUrl) : slide.imageUrl;
                    const variant = isMobile ? heroVariants?.mobile : heroVariants?.desktop;
                    const slideImage = index === 0 && variant?.source === originalImage ? variant.url : originalImage;
                    const slideSrcSet = index === 0 && variant?.source === originalImage ? variant.srcSet || undefined : undefined;
                    const btnX = isMobile ? (slide.mobileButtonX ?? slide.buttonX ?? 50) : (slide.buttonX ?? 50);
                    const btnY = isMobile ? (slide.mobileButtonY ?? slide.buttonY ?? 50) : (slide.buttonY ?? 50);
                    const btnScale = isMobile ? (slide.mobileButtonScale ?? slide.buttonScale ?? 100) : (slide.buttonScale ?? 100);
                    return (
                      <div
                        key={`${slide.imageUrl}-${index}`}
                        className={`absolute inset-0 ${hasCarouselStarted ? 'transition-opacity duration-1000' : 'transition-none'} ${index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
                        aria-hidden={index !== currentSlide}
                        inert={index !== currentSlide}
                      >
                        <img src={index === 0 || index === currentSlide || index === requestedSlide ? slideImage : undefined} srcSet={slideSrcSet} sizes={slideSrcSet ? '100vw' : undefined} alt={localized(content.hero?.title, language, `Banner ${index + 1}`)} width={isMobile ? 480 : 1440} height={isMobile ? 853 : 538} loading={index === 0 || index === currentSlide || index === requestedSlide ? 'eager' : 'lazy'} fetchPriority={index === 0 ? 'high' : 'low'} decoding="async" onLoad={() => { if (index === requestedSlide) setCurrentSlide(index); }} className="h-full w-full object-cover" />
                        {getOptionalHref(slide.linkUrl, language) && (
                          <a
                            href={getOptionalHref(slide.linkUrl, language)}
                            className="absolute inset-x-0 top-0 h-[70%]"
                            aria-label={language === 'en' ? `View ${localized(content.hero?.title, language, `banner ${index + 1}`)}` : `Ver ${localized(content.hero?.title, language, `banner ${index + 1}`)}`}
                          />
                        )}
                        <a
                          href={(getOptionalHref(content.hero?.ctaUrl, language) || getOptionalHref(slide.linkUrl, language)) || undefined}
                          onClick={(getOptionalHref(content.hero?.ctaUrl, language) || getOptionalHref(slide.linkUrl, language)) ? undefined : (e) => e.preventDefault()}
                          className="absolute z-20 inline-flex items-center bg-[#111011] font-bold text-white shadow-lg transition-colors hover:bg-[#FF50A3]"
                          style={{ 
                            left: `${btnX}%`, 
                            top: `${btnY}%`,
                            fontSize: 'clamp(10px, 1.5vw, 20px)',
                            padding: 'clamp(6px, 1vw, 14px) clamp(12px, 2.5vw, 32px)',
                            borderRadius: 'clamp(15px, 2vw, 25px)',
                            gap: 'clamp(4px, 0.75vw, 10px)',
                            transform: `translate(-50%, -50%) scale(${btnScale / 100})`,
                            transformOrigin: 'center'
                          }}
                        >
                          {localized(content.hero?.ctaLabel, language, t('home.viewCatalog'))}
                          <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="-rotate-45" style={{ width: 'clamp(12px, 1.4vw, 19px)', height: 'clamp(12px, 1.4vw, 19px)' }}>
                            <path d="M5 12h14M12 5l7 7-7 7" />
                          </svg>
                        </a>
                      </div>
                    );
                  })}

                  <button
                    onClick={() => {
                      setHasCarouselStarted(true);
                      setRequestedSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
                    }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-md transition-all text-slate-700 z-30"
                    aria-label="Anterior"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6" /></svg>
                  </button>
                  <button
                    onClick={() => {
                      setHasCarouselStarted(true);
                      setRequestedSlide((prev) => (prev + 1) % heroSlides.length);
                    }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-md transition-all text-slate-700 z-30"
                    aria-label="Siguiente"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6" /></svg>
                  </button>

                  <div className="absolute bottom-4 left-1/2 z-30 flex -translate-x-1/2 gap-2">
                    {heroSlides.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          setHasCarouselStarted(true);
                          setRequestedSlide(i);
                        }}
                        className="flex h-8 w-10 items-center justify-center"
                        aria-label={`Ir al slide ${i + 1}`}
                        aria-current={i === currentSlide ? 'true' : undefined}
                      >
                        <span className={`h-3 rounded-full shadow-sm transition-all ${i === currentSlide ? 'w-8 bg-[#FF50A3]' : 'w-3 bg-slate-300'}`} />
                      </button>
                    ))}
                  </div>
                </>
              ) : null}
            </div>
          </section>
        )}

        <div className="mx-auto max-w-[1350px] px-5 pb-6 md:px-[45px] md:pb-8">
          <h1 className="text-[20px] font-bold leading-snug text-black md:text-[26px]">
            {language === 'en' ? 'Official merch from your favorite creators' : 'Merch oficial de tus creadores favoritos'}
          </h1>
        </div>

        {content.hero?.isActive !== false && (
          <section className="w-full px-2 md:px-[47px]">
            <div className="w-full overflow-hidden rounded-[25px] bg-[#111011] py-5">
              <div className="animate-marquee flex w-max items-center text-white">
                <div className="flex items-center">
                  {marqueeLoopItems.map((name, index) => (
                    <span key={`first-${name}-${index}`} className="flex items-center gap-[30px] pr-[30px]">
                      <span className="text-[#FF50A3] text-[16px] font-semibold">*</span>
                      <span className="text-[25px] font-black uppercase tracking-wide">{name}</span>
                    </span>
                  ))}
                </div>
                <div className="flex items-center">
                  {marqueeLoopItems.map((name, index) => (
                    <span key={`second-${name}-${index}`} className="flex items-center gap-[30px] pr-[30px]">
                      <span className="text-[#FF50A3] text-[16px] font-semibold">*</span>
                      <span className="text-[25px] font-black uppercase tracking-wide">{name}</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {content.merch?.isActive !== false && (
          <section className="w-full px-2 md:px-[47px] py-10 md:py-14">
            <div className="mx-auto w-full max-w-[1345px]">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-[22px] font-bold text-black">
                  {localized(content.merch?.title, language, t('home.merchTitle'))}
                </h2>
                <a href={getOptionalHref(content.merch?.ctaUrl, language) || undefined} onClick={getOptionalHref(content.merch?.ctaUrl, language) ? undefined : (e) => e.preventDefault()} className="inline-flex items-center gap-2 text-[16px] font-medium text-input-pink transition-colors hover:text-[#B51B70]">
                  {localized(content.merch?.ctaLabel, language, t('common.viewMore'))}
                  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="-rotate-45">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </a>
              </div>

              <HomeProductRowsBoundary products={merchProducts} loading={productsLoading} />
            </div>
          </section>
        )}

        {content.creatorsFeature?.isActive !== false && (
          <section className="w-full px-2 md:px-[47px] py-10 md:py-14">
            <div className="mx-auto w-full max-w-[1345px] rounded-[25px] bg-white overflow-hidden flex flex-col lg:flex-row">
              <div className="w-full lg:w-1/2 shrink-0 flex flex-col justify-center px-6 md:px-12 lg:px-16 py-10 gap-[25px]">
                <div className="space-y-5">
                  <p className="text-center md:text-left text-[22px] font-normal text-black">
                    {localized(content.creatorsFeature?.eyebrow, language, 'CREADO PARA LA COMUNIDAD')}
                  </p>
                  <h2 className="w-full text-[32px] sm:text-[40px] md:text-[52px] font-bold leading-[1.3] text-black text-center md:text-left">
                    {localized(content.creatorsFeature?.title, language, 'Descubre productos unicos para ti')}
                  </h2>
                  <p className="w-full text-[16px] md:text-[18px] font-normal leading-relaxed text-black text-center md:text-left">
                    {localized(content.creatorsFeature?.description, language, 'Explora una seleccion especial de productos cuidadosamente elegidos para ofrecerte calidad, estilo y originalidad.')}
                  </p>
                </div>
                <div className="flex justify-center md:justify-start">
                  <a href={getOptionalHref(content.creatorsFeature?.ctaUrl, language) || undefined} onClick={getOptionalHref(content.creatorsFeature?.ctaUrl, language) ? undefined : (e) => e.preventDefault()} className="inline-flex h-[54px] w-[221px] items-center justify-center rounded-[25px] bg-black text-[18px] font-bold text-white transition-colors hover:bg-[#FF50A3]">
                    {localized(content.creatorsFeature?.ctaLabel, language, t('product.buyNow'))}
                  </a>
                </div>
              </div>
              <div className="flex-1 flex items-center justify-center p-5">
                <img
                  src={resolvePublicAssetUrl(content.creatorsFeature?.imageUrl || 'https://placehold.co/627x627/FF50A3/white?text=Community')}
                  alt={localized(content.creatorsFeature?.title, language, 'Community')}
                  width={627}
                  height={627}
                  loading="lazy"
                  decoding="async"
                  className="w-full max-w-[627px] aspect-square object-cover rounded-[25px]"
                />
              </div>
            </div>
          </section>
        )}

        {content.creators?.isActive !== false && (
          <section className="w-full px-2 md:px-[47px] py-10 md:py-14">
            <div className="mx-auto w-full max-w-[1345px]">
              <div className="w-full flex items-center justify-between">
                <h2 className="text-[22px] font-bold text-black">
                  {localized(content.creators?.title, language, 'Explora a nuestros creadores')}
                </h2>
                <a href={getOptionalHref(content.creators?.ctaUrl, language) || undefined} onClick={getOptionalHref(content.creators?.ctaUrl, language) ? undefined : (e) => e.preventDefault()} className="inline-flex items-center gap-2 text-[16px] font-medium text-input-pink transition-colors hover:text-[#B51B70]">
                  {localized(content.creators?.ctaLabel, language, t('common.viewMore'))}
                  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="-rotate-45">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </a>
              </div>

              <div className="mt-6 w-full flex gap-[15px] overflow-x-auto hide-scrollbar pb-4">
                {(content.creators?.items || []).map((creator, idx) => (
                  <CreatorCard key={`${localized(creator.name, language, '')}-${creator.href}-${idx}`} creator={creator} />
                ))}
              </div>
            </div>
          </section>
        )}

        {content.lastChance?.isActive !== false && latestProducts.length > 0 && (
          <section className="w-full px-2 md:px-[47px] py-12 md:py-16">
            <div className="mx-auto w-full max-w-[1345px]">
              <div className="mb-6 w-full flex items-center justify-between">
                <h2 className="text-[22px] font-bold text-black">{localized(content.lastChance?.title, language, t('home.lastChance'))}</h2>
                <a href={getOptionalHref(content.lastChance?.ctaUrl, language) || undefined} onClick={getOptionalHref(content.lastChance?.ctaUrl, language) ? undefined : (e) => e.preventDefault()} className="inline-flex items-center gap-2 text-[16px] font-medium text-input-pink transition-colors hover:text-[#B51B70]">
                  {localized(content.lastChance?.ctaLabel, language, t('common.viewMore'))}
                  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="-rotate-45">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </a>
              </div>

              <HomeProductRowsBoundary products={latestProducts} variant="lastChance" />
            </div>
          </section>
        )}

        {content.categories?.isActive !== false && (
          <section className="w-full px-2 md:px-[47px] py-14 md:py-20">
            <div className="mx-auto w-full max-w-[1345px]">
              <div className="mb-10 text-center">
                <div className="inline-flex items-center gap-3">
                  <span className="text-[22px] font-semibold text-[#FF50A4]">Ô£ª</span>
                  <h2 className="text-[32px] sm:text-[40px] font-bold uppercase text-black">{localized(content.categories?.title, language, t('home.categories'))}</h2>
                  <span className="text-[22px] font-semibold text-[#FF50A4]">Ô£ª</span>
                </div>
                <p className="mt-2 text-[16px] font-medium text-[#3F3F3F]">
                  {localized(content.categories?.subtitle, language, 'Explora nuestros productos favoritos para coleccionistas y fans')}
                </p>
              </div>

              <div className="grid gap-[15px] md:grid-cols-2">
                {categoryItems.map((item, index) => (
                  <CategoryCard key={`${localized(item.name, language, '')}-${index}`} item={item} />
                ))}
              </div>
            </div>
          </section>
        )}



        {content.upcoming?.isActive !== false && upcomingItems.length > 0 && (
          <section className="w-full px-2 md:px-[47px] py-6 md:py-8">
            <div className="mx-auto w-full max-w-[1345px]">
              <div className="mb-6 w-full flex items-center justify-between">
                <h2 className="text-[22px] font-bold text-black">{localized(content.upcoming?.title, language, t('home.upcoming'))}</h2>
                <a href={getOptionalHref(content.upcoming?.ctaUrl, language) || undefined} onClick={getOptionalHref(content.upcoming?.ctaUrl, language) ? undefined : (e) => e.preventDefault()} className="inline-flex items-center gap-2 text-[16px] font-medium text-input-pink transition-colors hover:text-[#B51B70]">
                  {localized(content.upcoming?.ctaLabel, language, t('common.viewMore'))}
                  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="-rotate-45">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </a>
              </div>

              <HomeProductRowsBoundary products={upcomingItems} variant="upcoming" />
            </div>
          </section>
        )}

        <HomePageDeferredBoundary content={content} />
      </main>

      <Footer content={content.footer} />
    </div>
  );
}

export default HomePage;
